import { BaseViewModel } from '../../../../../models/base/base-view-model';
import { Injectable } from '@angular/core';
import { User } from '../../../../../models/account/dto/user';
import { Event, NavigationEnd, Router } from '@angular/router';
import { NavItem } from '../nav-item/models/nav-item';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { CompanyDomainModel } from '../../../../../domainModels/company-domain-model';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ProviderUtils } from '../../../../../utils/provider-utils';
import { DistinctUtils } from '../../../../../utils/distinct-utils';
import { NavItemId } from '../../../../../models/enum/shared/nav-item-id.enum';
import { DefaultNavItem } from '../nav-item/models/default-nav-item';
import { ProductsNavItem } from '../nav-item/models/products-nav-item';
import { DefaultNavSubItem } from '../nav-sub-item/models/default-nav-sub-item';
import { IncompleteNavSubItem } from '../nav-sub-item/models/incomplete-nav-sub-item';
import { ProductsSubNavItem } from '../nav-sub-item/models/products-sub-nav-item';
import { NavSubItem } from '../nav-sub-item/models/nav-sub-item';
import { SettingsNavItem } from '../nav-item/models/settings-nav-item';
import { HelpNavItem } from '../nav-item/models/help-nav-item';
import { ProfileNavItem } from '../nav-item/models/profile-nav-item';
import { LocationNavItem } from '../nav-item/models/location-nav-item';
import { SignOutNavItem } from '../nav-item/models/sign-out-nav-item';

@Injectable()
export class SideNavBodyViewModel extends BaseViewModel {
  constructor(
    public userDomainModel: UserDomainModel,
    public companyDomainModel: CompanyDomainModel,
    private router: Router
  ) {
    super();
    this.bindUrlParams();
  }

  private readonly user$ = this.userDomainModel.user$;
  private readonly canUseTemplates$ = this.userDomainModel.canUseTemplates$;

  protected readonly _currentNavUrl = new BehaviorSubject<string>('');
  public readonly currentNavUrl$ = this._currentNavUrl.pipe(distinctUntilChanged());
  connectToCurrentNavUrl = (url: string) => this._currentNavUrl.next(url);

  private readonly allowIntegrationAccessForNonAdmins$ = this.companyDomainModel.inventoryProvider$.pipe(
    map(ip => ProviderUtils.supportsLocationSpecificPOSSyncing(ip))
  );

  public readonly topHalfNavItems$ = combineLatest([
    this.user$,
    this.allowIntegrationAccessForNonAdmins$,
    this.canUseTemplates$
  ]).pipe(
    map(([user, allowNonAdminIntegrationAccess, supportsTemplates]) => {
      return this.getTopHalfNavItems(user, allowNonAdminIntegrationAccess, supportsTemplates);
    }),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray)
  );

  public readonly bottomHalfNavItems$ = of(this.getBottomHalfNavItems());

  private urlChanged(nav: NavigationEnd) {
    this._currentNavUrl.next(nav.url);
  }

  private bindUrlParams() {
    this._currentNavUrl.next(this.router.url);
    this.router.events.pipe(filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd)).subscribeWhileAlive({
      owner: this,
      next: evt => this.connectToCurrentNavUrl(evt?.url)
    });
  }

  private getTopHalfNavItems(
    user: User,
    allowNonAdminIntegrationsAccess: boolean,
    companySupportsTemplates: boolean
  ): NavItem[] {
    const isSuperAdmin = user?.isSuperAdmin();
    const canAccessSettings = user?.canAccessSettings();
    return [
      this.getHomeNavItem(),
      this.getHighGuideNavItem(),
      this.getProductsNavItem(),
      ...(isSuperAdmin && companySupportsTemplates ? [this.getTemplatesNavItem()] : []),
      this.getMenusNavItem(),
      this.getDisplaysNavItem(),
      this.getSettingsNavItem(canAccessSettings, allowNonAdminIntegrationsAccess)
    ];
  }

  private getBottomHalfNavItems(): NavItem[] {
    return [
      this.getHelpNavItem(),
      this.getProfileNavItem(),
      this.getCurrentLocationNavItem(),
      this.getSignOutNavItem()
    ];
  }

  private getHomeNavItem(): NavItem {
    const nonFilledIcon = 'assets/icons/light/outline/home.svg';
    const filledIcon = 'assets/icons/light/solid/home.svg';
    return new DefaultNavItem(NavItemId.Home, 'Home', [], '/home', nonFilledIcon, filledIcon);
  }

  private getHighGuideNavItem(): NavItem {
    const nonFilledIcon = 'assets/svg/budsense/highguide.svg';
    const filledIcon = 'assets/svg/budsense/highguide.svg';
    const item = new DefaultNavItem(NavItemId.HighGuide, 'High Guide', [], '/high-guide', nonFilledIcon, filledIcon);
    item.labelRightPadding = '0';
    return item;
  }

  private getProductsNavItem(): ProductsNavItem {
    const products = new DefaultNavSubItem('All Products', 'all-products');
    const incomplete = new IncompleteNavSubItem('Incomplete', 'incomplete');
    const orderReceiving = new DefaultNavSubItem('Order Receiving', 'order-receiving');
    const subItems = [products, incomplete, orderReceiving];
    const nonFilled = 'assets/icons/light/outline/view-grid.svg';
    const filled = 'assets/icons/light/solid/view-grid.svg';
    return new ProductsNavItem(NavItemId.Products, 'Products', subItems, '/products', nonFilled, filled);
  }

  private getTemplatesNavItem(): NavItem {
    const digital = new DefaultNavSubItem('Digital', 'digital');
    const print = new DefaultNavSubItem('Print', 'print');
    const web = new DefaultNavSubItem('Web', 'web');
    const subItems = [digital, print, web];
    const nonFilled = 'assets/icons/light/outline/template.svg';
    const filled = 'assets/icons/light/solid/template.svg';
    return new DefaultNavItem(NavItemId.Templates, 'Templates', subItems, '/templates', nonFilled, filled);
  }

  private getMenusNavItem(): NavItem {
    const digital = new DefaultNavSubItem('Digital', 'digital');
    const print = new DefaultNavSubItem('Print', 'print');
    const web = new DefaultNavSubItem('Web', 'web');
    const subItems = [digital, print, web];
    const nonFilled = 'assets/icons/light/outline/document-text.svg';
    const filled = 'assets/icons/light/solid/document-text.svg';
    return new DefaultNavItem(NavItemId.Menus, 'Menus', subItems, '/menus', nonFilled, filled);
  }

  private getDisplaysNavItem(): NavItem {
    const nonFilled = 'assets/icons/light/outline/device-tablet.svg';
    const filled = 'assets/icons/light/solid/device-tablet.svg';
    return new DefaultNavItem(NavItemId.Displays, 'Displays', [], '/displays', nonFilled, filled);
  }

  private getSettingsNavItem(canAccessSettings: boolean, allowNonAdminIntegrationsAccess: boolean): NavItem {
    const nonFilled = 'assets/icons/light/outline/cog.svg';
    const filled = 'assets/icons/light/solid/cog.svg';
    const general = new DefaultNavSubItem('General', 'general');
    const highGuide = new DefaultNavSubItem('HighGuide', 'high-guide');
    const customization = new DefaultNavSubItem('Customization', 'customization');
    const menus = new DefaultNavSubItem('Menus', 'menus');
    const products = new ProductsSubNavItem('Products', 'products');
    const automation = new DefaultNavSubItem('Automation', 'automation');
    const mediaManager = new DefaultNavSubItem('Media Manager', 'media-manager');
    let subItems: NavSubItem[];
    if (canAccessSettings) {
      subItems = [general, highGuide, customization, menus, products, automation, mediaManager];
    } else {
      subItems = allowNonAdminIntegrationsAccess ? [general, products] : [products];
    }
    return new SettingsNavItem(NavItemId.Settings, 'Settings', subItems, '/settings', nonFilled, filled);
  }

  private getHelpNavItem(): HelpNavItem {
    const nonFilled = 'assets/icons/light/outline/question-mark-circle.svg';
    const filled = 'assets/icons/light/solid/question-mark-circle.svg';
    return new HelpNavItem(NavItemId.Help, 'Help', [], 'https://support.mybudsense.com', nonFilled, filled);
  }

  private getProfileNavItem(): ProfileNavItem {
    const nonFilled = 'assets/icons/light/outline/user.svg';
    const filled = 'assets/icons/light/solid/user.svg';
    return new ProfileNavItem(NavItemId.Profile, 'Profile', [], '/account', nonFilled, filled);
  }

  private getCurrentLocationNavItem(): LocationNavItem {
    const nonFilled = 'assets/icons/light/outline/status-online.svg';
    const filled = 'assets/icons/light/solid/status-online.svg';
    const path = 'locations-picker-modals';
    return new LocationNavItem(NavItemId.CurrentLocation, 'Current Location', [], path, nonFilled, filled);
  }

  private getSignOutNavItem(): SignOutNavItem {
    const nonFilled = 'assets/icons/light/outline/logout.svg';
    const filled = 'assets/icons/light/solid/logout.svg';
    return new SignOutNavItem(NavItemId.SignOut, 'Sign Out', [], '/auth/sign-out', nonFilled, filled);
  }
}
